import {
  all, call, put, select, take,
} from 'redux-saga/effects';
import {
  ceil, compose, filter,
  get, keyBy,
  map, mapValues, range,
  reduce, size,
} from 'lodash/fp';

import * as adminServices from '../admin/services';
import { FIREBASE_REF } from '../common/constants';
import firebase from '../common/firebase';
import * as contactsServices from '../contacts/services';
import * as errorActions from '../errors/actions';
import * as notificationActions from '../notification/actions';
import * as actions from './actions';

export function* fetchTargets() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_TARGETS_REQUEST);

      const response = yield call(contactsServices.fetchContactsBySchool, action.payload);

      const responses = yield all(
        compose(
          map(number => call(contactsServices.fetchContactsBySchool, { ...action.payload, pageNumber: number + 1 })),
          range(1),
          ceil,
        )(response.headers.total / response.headers['per-page'] || 1),
      );

      const targets = [...response.data, ...reduce((acc, cur) => [...acc, ...cur.data], [], responses)];

      yield put(actions.fetchTargetsSuccess({ targets }));
    } catch (error) {
      yield put(actions.fetchTargetsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* createNewsletter() {
  while (true) {
    try {
      const action = yield take(actions.CREATE_NEWSLETTER_REQUEST);

      const { values, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const writer = yield select(state => state.user.info.name);

      const createdAt = yield call(firebase.firestore.FieldValue.serverTimestamp);

      const adminRelationships = yield select(state => state.admin.adminRelationships);

      const school = adminRelationships.data.org;

      const newsletter = {
        school: sid,
        schoolName: get('name', school),
        title: values.title,
        description: values.description,
        number: values.number,
        sendAt: Date.now(),
        targets: compose(
          mapValues(() => false),
          keyBy(target => target.primary_guardian_contact.id),
          // TODO: 대표 보호자 연락처가 없는 경우 연락처 임시 제거
          filter(target => target.primary_guardian_contact),
        )(values.targets),
        targetCount: size(filter(target => target.primary_guardian_contact, values.targets)),
        recordCount: 0,
        status: 'ongoing',
        lastSendAt: createdAt,
        hasSurvey: !!(values.questions || values.signatures),
        createdAt,
        writer,
      };

      const ref = yield call(firebase.firestore.collection, `${FIREBASE_REF}/school-newsletters`);
      const nid = ref.doc().id;

      const targets = compose(
        reduce((acc, cur) => ({
          ...acc,
          [get('primary_guardian_contact.id', cur) || get('guardian_contacts[0].id', cur)]: {
            cid: cur.id,
            clazzGroup: cur.class_group
              ? { id: cur.class_group.id, grade: cur.class_group.grade, clazzName: cur.class_group.class_name }
              : null,
            customGroups: map(customGroup => ({
              id: customGroup.id,
              name: customGroup.name,
              isUnspecifiedGroup: customGroup.is_unspecified_group,
            }), cur.custom_groups),
            primaryGuardian: {
              id: cur.primary_guardian_contact.id,
              name: cur.primary_guardian_contact.name,
              relationship: cur.primary_guardian_contact.relationship,
              phoneNumber: cur.primary_guardian_contact.mobile,
              user: cur.primary_guardian_contact.user,
              userId: cur.primary_guardian_contact.user_id,
            },
            clazzNumber: cur.number,
            name: cur.name,
            phoneNumber: cur.mobile,
            recorded: false,
          },
        }), {}),
        filter(target => target.primary_guardian_contact),
      )(values.targets);

      const newsletterRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/school-newsletters/${nid}`);
      const surveyRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/surveys/${nid}`);
      const targetRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/targets/${nid}`);
      const storageRef = yield call(firebase.storage.ref, `${FIREBASE_REF}/schools/${sid}/school-newsletters/${nid}.pdf`);

      yield call(
        firebase.storage.put,
        storageRef,
        values.file,
        { customMetadata: { name: values.file.name } },
      );

      const batch = yield call(firebase.firestore.batch);

      batch.set(newsletterRef, newsletter);
      batch.set(surveyRef, { questions: values.questions, signatures: values.signatures });
      batch.set(targetRef, { data: targets });

      yield call([batch, 'commit']);

      yield put(notificationActions.sendNewsletterNotificationRequest({
        newsletter: { id: nid, title: values.title },
        sid,
        targets,
      }));

      yield put(actions.createNewsletterSuccess());
    } catch (error) {
      yield put(actions.createNewsletterFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}
